import { Injectable } from '@angular/core';
import { PDFDocument } from '../interfaces/pdf-document.interface';
import {
  TabSpec,
  SectionSpec,
  TextSection,
  ListSection,
  CardSection,
  TableSection,
  ListItem
} from '../interfaces/report-spec.interface';
import { PdfTableService } from './pdf-table.service';
import { MarkdownRendererComponent } from '../../shared/components/markdown-renderer/markdown-renderer.component';

/**
 * Universal PDF Renderer Service
 *
 * Renders report tabs (with nested sections) to PDF format using PDFKit.
 * This service interprets the declarative tab spec format and generates
 * the corresponding PDF output.
 *
 * Supports 4 universal component types:
 * - Text: Formatted text/markdown with size variants
 * - List: Smart lists (bullets or key-value fields)
 * - Card: Container with title and child content (recursive)
 * - Table: True tabular data (rows × columns)
 */
@Injectable({
  providedIn: 'root'
})
export class UniversalPdfRendererService {
  private readonly marginLeft = 50;
  private readonly marginRight = 50;
  private readonly pageWidth = 612; // US Letter width in points
  private readonly contentWidth = this.pageWidth - this.marginLeft - this.marginRight;
  private readonly cardHeaderHeight = 40;

  /**
   * Render a single tab (with its sections) to PDF
   * @param doc - PDFKit document instance
   * @param tab - Tab specification to render
   */
  renderTab(doc: PDFDocument, tab: TabSpec): void {
    for (const section of tab.sections) {
      this.renderSection(doc, section);
      doc.moveDown(2);
    }
  }

  /**
   * Render a single section based on its type (recursive for cards)
   */
  private renderSection(doc: PDFDocument, section: SectionSpec): void {
    switch (section.type) {
      case 'text':
        this.renderText(doc, section as TextSection);
        break;
      case 'list':
        this.renderList(doc, section as ListSection);
        break;
      case 'card':
        this.renderCard(doc, section as CardSection);
        break;
      case 'table':
        this.renderTable(doc, section as TableSection);
        break;
    }
  }

  /**
   * Render a text section
   * Supports variants: title, heading, body
   * Uses markdown rendering for all text content
   */
  private renderText(doc: PDFDocument, section: TextSection): void {
    // Use markdown renderer for all text content
    MarkdownRendererComponent.renderToPdf(doc, section.content, this.marginLeft, this.contentWidth);

    const variant = section.variant || 'body';
    doc.moveDown(variant === 'title' ? 2 : variant === 'heading' ? 1.5 : 1);
  }

  /**
   * Render a list section
   * Smart rendering: checks if items are labeled fields or simple bullets
   */
  private renderList(doc: PDFDocument, section: ListSection): void {
    if (section.items.length === 0) return;

    const isFieldList = this.isFieldList(section.items);

    if (isFieldList) {
      // Render as key-value table
      const rows = section.items.map(item => {
        const typedItem = item as { label?: string; value: string; metadata?: string };
        const label = typedItem.label ? typedItem.label + ':' : '';
        const value = typedItem.metadata
          ? `${typedItem.value} (${typedItem.metadata})`
          : typedItem.value;
        return [label, value];
      });

      const currentY = PdfTableService.drawTable(doc, ['', ''], rows, doc.y);
      doc.y = currentY;
    } else {
      // Render as bullet list
      for (const item of section.items) {
        const value = typeof item === 'string' ? item : item.value;
        const metadata = typeof item !== 'string' && item.metadata ? ` (${item.metadata})` : '';

        doc.fontSize(10)
           .font('Helvetica')
           .fill('#000000')
           .text(`• ${value}${metadata}`, this.marginLeft + 20, doc.y, {
             width: this.contentWidth - 20
           });
        doc.moveDown(0.5);
      }
    }
  }

  /**
   * Render a card section
   * Container with optional title and recursive child content
   */
  private renderCard(doc: PDFDocument, section: CardSection): void {
    // Render card header (if title exists)
    if (section.title) {
      this.ensureSpace(doc, this.cardHeaderHeight);
      const headerY = doc.y;

      doc.rect(this.marginLeft, headerY, this.contentWidth, 30)
         .fill('#172554');

      doc.fontSize(14)
         .font('Helvetica-Bold')
         .fill('#ffffff')
         .text(section.title, this.marginLeft, headerY + 8, {
            align: 'center',
            width: this.contentWidth
          });

      doc.y = headerY + this.cardHeaderHeight;
      doc.fill('#000000');
    }

    // Recursively render child content
    for (const child of section.content) {
      this.renderSection(doc, child);
    }

    doc.moveDown(1);
  }

  /**
   * Render a table section (true tabular data)
   * Displays multi-column data with headers
   */
  private renderTable(doc: PDFDocument, section: TableSection): void {
    // Render section header (if title exists)
    if (section.title) {
      this.ensureSpace(doc, this.cardHeaderHeight);
      const headerY = doc.y;

      doc.rect(this.marginLeft, headerY, this.contentWidth, 30)
         .fill('#172554');

      doc.fontSize(14)
         .font('Helvetica-Bold')
         .fill('#ffffff')
         .text(section.title, this.marginLeft, headerY + 8, {
            align: 'center',
            width: this.contentWidth
          });

      doc.y = headerY + this.cardHeaderHeight;
      doc.fill('#000000');
    }

    // Render table with column headers
    const currentY = PdfTableService.drawTable(doc, section.columns, section.rows, doc.y);
    doc.y = currentY;
  }

  private ensureSpace(doc: PDFDocument, requiredHeight: number): void {
    const availableHeight = doc.page.height - doc.page.margins.bottom;

    if (doc.y + requiredHeight > availableHeight) {
      doc.addPage();
      doc.y = doc.page.margins.top;
    }
  }

  /**
   * Helper to check if list items should be rendered as field list
   */
  private isFieldList(items: ListItem[]): boolean {
    return items.some(item =>
      typeof item !== 'string' && item.label !== undefined
    );
  }
}
